import React, { useEffect, useState, useRef } from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import Banner_Container from "../../Banners/container";
import InventoryList from "./InventoryList";
import ReplaceCartModal from "../../PopUp/ReplaceCartPopUp";
import { useApiRequest } from "../Login/Hooks/useApiRequest";
import TodaySpecialComponent from "./TodaySpecial/component";
import Procced from "../../ProccedToPay/component";
import BuyAgain from "./BuyAgain/Component";
import SearchBar from "./SearchBar/Component";
import VmInventoryHeader from "../../Header/component";
import PendingPickup from "../PendingPickup/component";
import WaitingLoder from "../../Loader/component";
import QRModal from "../VmList/Qr";
import SortFilterDrawer from "./Filter";
import NoActiveMachine from "./NoActiveMachine";
import { API_BASE_URL } from "../../config";

import "./vmlist.css";

// Machine ID that routes directly to items without QR flow
const DIRECT_ITEM_MACHINE_ID = 7;

// ─── Pure helpers ────────────────────────────────────────────────────────────

const applyCartCounts = (inventory = [], cartItems = []) =>
  inventory.map((item) => {
    const match = cartItems.find(
      (c) => parseInt(c.slotId) === parseInt(item.slot_id)
    );
    return { ...item, mycount: match ? match.count : item.mycount ?? 0 };
  });

const getSortParams = (value) => {
  const map = {
    "price-low": { sort_by: "price", sort_order: "asc" },
    "price-high": { sort_by: "price", sort_order: "desc" },
    "stack-top": { sort_by: "slot", sort_order: "asc" },
    "stack-bottom": { sort_by: "slot", sort_order: "desc" },
  };
  return map[value] ?? {};
};

const cartTotal = (items = []) =>
  items.reduce((sum, item) => sum + item.amount * item.count, 0);

// ─── Component ───────────────────────────────────────────────────────────────

const VmInventoryContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { makeApiRequest } = useApiRequest();

  const searchParams = new URLSearchParams(location.search);
  const machineId = searchParams.get("machineId");
  const uuid = searchParams.get("value");
  const showItems = searchParams.get("showItems");

  // ── Machine / location state ──────────────────────────────────────────────
  const [vmName, setVmName] = useState(null);
  const [vmAddress, setVmAddress] = useState(null);
  const [uuidVmId, setUuidVmId] = useState(null);
  const [machineUuidFlag, setMachineUuidFlag] = useState(false);
  const [fUuid, setFUuid] = useState(null);

  // ── Inventory state ───────────────────────────────────────────────────────
  const [vmInventory, setVmInventory] = useState([]);
  const [vmInventoryWithCount, setVmInventoryWithCount] = useState([]);
  const [todaySpecialInventory, setTodaySpecialInventory] = useState([]);
  const [buyAgainList, setBuyAgainList] = useState([]);
  const [buyAgainWithCount, setBuyAgainWithCount] = useState([]);
  const [comboList, setComboList] = useState([]);
  const [comboListWithCount, setComboListWithCount] = useState([]);
  const [filterData, setFilterData] = useState(null);
  const [loadingVmData, setLoadingVmData] = useState(false);
  const [inventoryMsg] = useState("Loading Data...");

  // ── Cart state ────────────────────────────────────────────────────────────
  const [preCart, setPreCart] = useState([]);
  const [preCartCombo, setPreCartCombo] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [replaceSlot, setReplaceSlot] = useState(null);
  const [replaceCard, setReplaceCard] = useState(false);

  // ── Orders ────────────────────────────────────────────────────────────────
  const [activeOrders, setActiveOrders] = useState([]);

  // ── UI / loading flags ────────────────────────────────────────────────────
  const [loader, setLoader] = useState(false);
  const [qrModalFlag, setQrModalFlag] = useState(false);
  const [filterFlag, setFilterFlag] = useState(false);
  const [showSearchField, setShowSearchField] = useState(false);
  const [searchTerms, setSearchTerms] = useState(null);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [tab, setTab] = useState("filters");
  const [categories, setCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [sortValue, setSortValue] = useState("");
  const [priceRange, setPriceRange] = useState([0, 250]);
  const [trayLevel, setTrayLevel] = useState("all");

  const comboFetchedRef = useRef(false);

  // ── Persist machineId to localStorage ────────────────────────────────────
  useEffect(() => {
    if (machineId) localStorage.setItem("machineId", machineId);
  }, [machineId]);

  // ── Route guard on mount ──────────────────────────────────────────────────
  useEffect(() => {
    const isMachineIdValid =
      machineId &&
      machineId.toLowerCase() !== "null" &&
      machineId.toLowerCase() !== "false";

    const qrRoute = `/entryPointQr?route=${encodeURIComponent(
      `machineId=${machineId}&value=${uuid}`
    )}`;

    if (isMachineIdValid) {
      if (!showItems) {
        const id = parseInt(machineId, 10);
        if (id === DIRECT_ITEM_MACHINE_ID) {
          navigate(`/items?machineId=${machineId}&value=${uuid}&showItems=true`);
        } else {
          navigate(qrRoute);
        }
      }
    } else if (uuid && !showItems) {
      resolveMachineByUUID(uuid);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch items when machineId is ready ───────────────────────────────────
  useEffect(() => {
    if (machineId) fetchItems();
  }, [machineId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Side-fetches once vmInventory loads ───────────────────────────────────
  useEffect(() => {
    if (vmInventory.length > 0) {
      fetchComboInventory();
      fetchTodaySpecial();
      fetchActiveOrders();
    }
  }, [vmInventory]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-fetch buy-again when uuidVmId resolves ─────────────────────────────
  useEffect(() => {
    if (uuidVmId) fetchBuyAgain();
  }, [uuidVmId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync inventory lists with counts ─────────────────────────────────────
  useEffect(() => {
    setVmInventoryWithCount(withZeroCount(vmInventory));
    setBuyAgainWithCount(withZeroCount(buyAgainList));
    setComboListWithCount(withZeroCount(comboList));
  }, [vmInventory, buyAgainList, comboList]);

  // ─── Inventory count utilities ────────────────────────────────────────────

  const withZeroCount = (list) =>
    list.map((item) => ({ ...item, mycount: item.mycount ?? 0 }));

  /**
   * Apply updated cart counts to every inventory list at once.
   * Called after every successful cart mutation or preCart fetch.
   */
  const updateAllInventories = (cartItems, currentMachineId) => {
    if (currentMachineId !== machineId) return;

    const apply = (setter) =>
      setter((prev) => (prev ? applyCartCounts(prev, cartItems) : prev));

    apply(setVmInventoryWithCount);
    apply(setTodaySpecialInventory);
    apply(setBuyAgainWithCount);
    apply(setFilterData);
  };

  // ─── API: resolve machine UUID → machineId ────────────────────────────────

  const resolveMachineByUUID = (targetUuid) => {
    const token = localStorage.getItem("UserJWTAuth");
    setFUuid(targetUuid);

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/outlet/machinebyuuid/${targetUuid}`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      handleSuccess: (res) => {
        const id = res?.data?.data?.machineId;
        if (id) {
          setUuidVmId(id);
          navigate(`/items?machineId=${id}&value=${targetUuid}&showItems=true`);
        } else {
          toast.error("Machine not found for this device", {
            position: "top-right",
            autoClose: 1500,
          });
        }
      },
      handleError: (err) => {
        const code = err?.response?.data?.status?.code;
        if (code === "UNAUTHORIZED") {
          navigate(`/login?value=${targetUuid}`);
        } else {
          setMachineUuidFlag(true);
        }
      },
    });
  };

  // ─── API: fetch inventory items (with optional filters) ───────────────────

  const fetchItems = ({ applyFilter = false } = {}) => {
    if (!machineId) return;
    setLoader(true);

    const token = localStorage.getItem("UserJWTAuth");
    const deviceId = localStorage.getItem("uuid");
    const params = new URLSearchParams({ exclude_inactive: "false" });

    if (applyFilter) {
      params.set("price_min", priceRange[0]);
      params.set("price_max", priceRange[1]);
      const { sort_by, sort_order } = getSortParams(sortValue);
      if (sort_by) params.set("sort_by", sort_by);
      if (sort_order) params.set("sort_order", sort_order);
      if (trayLevel && trayLevel !== "all") params.set("tray_level", trayLevel);
      if (selectedCategoryIds.length > 0)
        params.set("category_id_arr", selectedCategoryIds.join("|"));
    }

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/outlet/${machineId}/items?${params}`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId,
      },
      handleSuccess: (res) => {
        setVmName(res?.headers?.name);
        setVmAddress(res?.headers?.address);

        const items = (res?.data?.data ?? []).map((item) => ({
          ...item,
          mycount: 0,
        }));

        if (applyFilter) {
          setFilterData(items);
        } else {
          setVmInventory(items);
        }

        setLoadingVmData(true);
        fetchPreCart();
        setLoader(false);
      },
      handleError: (err) =>{ 
        console.log("error-------",err?.response?.data?.status?.code =="UNAUTHORIZED")
 if(err?.response?.data?.status?.code =="UNAUTHORIZED"){
  navigate('/login')
 }
        setLoader(false)
        
      }
    });
  };

  // ─── API: fetch combo inventory (once per mount) ──────────────────────────

  const fetchComboInventory = () => {
    if (comboFetchedRef.current) return;
    comboFetchedRef.current = true;

    const token = localStorage.getItem("UserJWTAuth");
    if (!token) return;

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/outlet/${machineId}/combo`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      handleSuccess: (res) => setComboList(res.data.data ?? []),
      handleError: () => {},
    });
  };

  // ─── API: fetch today's special ───────────────────────────────────────────

  const fetchTodaySpecial = () => {
    const token = localStorage.getItem("UserJWTAuth");
    if (!token) return;

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/outlet/${machineId}/special`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
      },
      handleSuccess: (res) => {
        const items = (res?.data?.data ?? []).map((item) => ({
          ...item,
          mycount: 0,
        }));
        setTodaySpecialInventory(
          preCart.length > 0 ? applyCartCounts(items, preCart) : items
        );
        fetchPreCart();
      },
      handleError: (err) => {
        toast.error(err?.response?.data?.status?.message ?? "Error", {
          position: "top-right",
          autoClose: 1500,
        });
      },
    });
  };

  // ─── API: fetch pre-cart ──────────────────────────────────────────────────

  const fetchPreCart = () => {
    const token = localStorage.getItem("UserJWTAuth");
    if (!token) return;

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/cart`,
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      handleSuccess: (res) => {
        const cartItems = res?.data?.data?.items ?? [];
        const preMachineId = res?.headers?.["machineid"];

        setTotalAmount(cartTotal(cartItems));
        setPreCart(cartItems);
        setPreCartCombo(res?.data?.data?.comboDetails ?? []);
        updateAllInventories(cartItems, preMachineId);
      },
      handleError: (err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("UserJWTAuth");
        }
      },
    });
  };

  // ─── API: fetch active orders ─────────────────────────────────────────────

  const fetchActiveOrders = () => {
    const token = localStorage.getItem("UserJWTAuth");
    const deviceId = localStorage.getItem("uuid");

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/order/active`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId,
        accept: "application/json, text/plain, */*",
      },
      handleSuccess: (res) => setActiveOrders(res?.data?.data ?? []),
      handleError: () => {
        toast.error("Unable to fetch active orders.", {
          position: "top-right",
          autoClose: 1500,
        });
      },
    });
  };

  // ─── API: fetch buy-again list ────────────────────────────────────────────

  const fetchBuyAgain = () => {
    const token = localStorage.getItem("UserJWTAuth");
    const deviceId = localStorage.getItem("uuid");

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/outlet/${machineId}/previously-purchased`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId ?? "",
      },
      handleSuccess: (res) => setBuyAgainList(res?.data?.data ?? []),
      handleError: () => {},
    });
  };

  // ─── API: fetch categories ────────────────────────────────────────────────

  const fetchCategories = () => {
    setLoader(true);
    const token = localStorage.getItem("UserJWTAuth");
    const deviceId = localStorage.getItem("uuid");

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/item/category`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-app-platform": "web",
        "x-app-version": "v1.0.1",
        "x-device-id": deviceId,
      },
      handleSuccess: (res) => {
        setCategories(res?.data?.data ?? []);
        setLoader(false);
      },
      handleError: () => setLoader(false),
    });
  };

  // ─── Cart operations ──────────────────────────────────────────────────────

  const handleCartOperation = (e, _index, slotId, currentCount, operation) => {
    e.stopPropagation();
    setLoader(true);

    const token = localStorage.getItem("UserJWTAuth");
    const payloadCount =
      operation === "add" ? currentCount + 1 : currentCount - 1;

    if (preCart.length > 0 || preCartCombo.length > 0) {
      setReplaceSlot({ slotId, index: _index });
    }

    makeApiRequest({
      url: `${API_BASE_URL}/consumerbe/api/v2/cart?machineId=${machineId}&replace-cart=${replaceCard}`,
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
      data: [{ slotId: parseInt(slotId), count: payloadCount }],
      handleSuccess: (res) => {
        setLoader(false);
        localStorage.setItem("card_id", res.headers["cart-id"]);

        const cartItems = res?.data?.data?.items ?? [];
        const matched = cartItems.find(
          (item) => parseInt(item.slotId) === parseInt(slotId)
        );

        const applyOp = (item) => {
          if (item.slot_id !== slotId) return item;
          const next = matched
            ? matched.count
            : operation === "add"
            ? item.mycount + 1
            : Math.max(0, item.mycount - 1);
          return { ...item, mycount: next };
        };

        setVmInventoryWithCount((prev) => prev.map(applyOp));
        setTodaySpecialInventory((prev) => prev.map(applyOp));
        setBuyAgainWithCount((prev) => prev.map(applyOp));
        setFilterData((prev) => (prev ? prev.map(applyOp) : prev));

        fetchPreCart();
      },
      handleError: (err) => {
        setLoader(false);
        if (
          err?.response?.data?.status?.message ===
          "Cart contains items from another machine"
        ) {
          setReplaceCard(true);
        }
        toast.error(err?.response?.data?.status?.message, {
          position: "top-right",
          autoClose: 1500,
        });
      },
    });
  };

  const replaceMyCardFun = (e) => {
    setReplaceCard(false);
    if (replaceSlot) {
      handleCartOperation(e, replaceSlot.index, replaceSlot.slotId, 0, "add");
    }
  };

  // ─── Filter handlers ──────────────────────────────────────────────────────

  const handleCategoryToggle = (id) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleReset = () => {
    setFilterData(null);
    setSelectedCategoryIds([]);
    setSortValue("");
    setPriceRange([0, 250]);
    setTrayLevel("all");
    fetchItems();
    closeFilter();
  };

  const applyFilter = () => {
    setLoader(true);
    fetchItems({ applyFilter: true });
    closeFilter();
  };

  // ─── UI helpers ───────────────────────────────────────────────────────────

  const closeFilter = () => setFilterFlag(false);
  const goToHome = () => navigate("/");

  const filteredProducts = vmInventoryWithCount.filter((p) =>
    p.mv_name.toLowerCase().includes(searchTerms?.toLowerCase() ?? "")
  );

  // Calculate bottom padding based on active overlays
  const bottomPadding = (() => {
    const hasOrders = activeOrders?.length > 0;
    const hasCart = preCart?.length > 0;
    if (hasCart && hasOrders) return "135px";
    if (hasCart) return "50px";
    if (hasOrders && activeOrders.length > 1) return "115px";
    if (hasOrders) return "80px";
    return "0px";
  })();

  // ─── Early returns ────────────────────────────────────────────────────────

  if (qrModalFlag) {
    return (
      <QRModal
        ShowQRFun={() => setQrModalFlag(true)}
        qrModelflag={qrModalFlag}
        CloseQrFun={() => setQrModalFlag(false)}
        preCart={preCart}
        fetchMachineByUUID={resolveMachineByUUID}
      />
    );
  }

  if (machineUuidFlag) {
    return <NoActiveMachine uuid={fUuid} gotohome={goToHome} />;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const displayItems = filterData
    ? filterData
    : searchTerms
    ? filteredProducts
    : vmInventoryWithCount;

  return (
    <>
      <SortFilterDrawer
        open={filterFlag}
        onClose={closeFilter}
        ApplyFillter={applyFilter}
        categories={categories}
        selectedCategoryIds={selectedCategoryIds}
        sortValue={sortValue}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        trayLevel={trayLevel}
        setTrayLevel={setTrayLevel}
        setSortValue={setSortValue}
        tab={tab}
        setTab={setTab}
        handleReset={handleReset}
        handleCategoryToggle={handleCategoryToggle}
      />

      {loader && <WaitingLoder />}

      <VmInventoryHeader
        title={vmName}
        subtitle={vmAddress}
        route="/"
        ShowQRFun={() => setQrModalFlag(true)}
        preCart={preCart}
      />

      <Box
        sx={{
          display: { xs: "block", sm: "block", md: "" },
          background:
            "radial-gradient(circle, rgba(245, 233, 233, 0.408) 0%, rgba(185, 245, 247, 0.34) 100%)",
          minHeight: "100vh",
          padding: "12px",
          color: "#fff",
        }}
      >
        {replaceCard && (
          <ReplaceCartModal
            replaceMyCardFun={replaceMyCardFun}
            setReplaceCard={setReplaceCard}
          />
        )}

        <div style={{ paddingTop: "50px" }}>
          <Banner_Container vmid={machineId ?? 0} />
        </div>

        {!searchTerms && (
          <BuyAgain
            buyAgainList={buyAgainWithCount}
            onIncrement={handleCartOperation}
            onDecrement={handleCartOperation}
          />
        )}

        {!searchTerms && (
          <TodaySpecialComponent
            onIncrement={handleCartOperation}
            onDecrement={handleCartOperation}
            data={todaySpecialInventory}
          />
        )}

        <div style={{ paddingBottom: bottomPadding }}>
          {showSearchField && (
            <SearchBar
              setSearchTerms={setSearchTerms}
              searchTerms={searchTerms}
              setShowSearchfiled={setShowSearchField}
            />
          )}

          <InventoryList
            items={displayItems}
            onIncrement={handleCartOperation}
            onDecrement={handleCartOperation}
            setFilterFlag={setFilterFlag}
            setSearchTerms={setSearchTerms}
            searchTerms={searchTerms}
            showSearchfiled={showSearchField}
            setShowSearchfiled={setShowSearchField}
            fetchCategories={fetchCategories}
            inventroyMsg={inventoryMsg}
            loadingVmdata={loadingVmData}
          />
        </div>
      </Box>

      {activeOrders?.[0] && (
        <PendingPickup
          preCart={preCart?.[0] ? preCart : undefined}
          activeOrders={activeOrders}
          page="vmItems"
        />
      )}

      {preCart?.[0] && (
        <Procced
          preCart={preCart}
          totalAmount={totalAmount}
          page="vmItems"
        />
      )}
    </>
  );
};

export default VmInventoryContainer;
